/**
 * TimeWithLocalHover Component (PE-1450)
 *
 * Displays time in its original timezone with abbreviation.
 * On hover, shows a tooltip with the time converted to the viewer's local timezone.
 */

import React, { useState, useMemo } from 'react';

const TIMEZONE_ABBR: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Los_Angeles': 'PT',
  'Europe/London': 'GMT',
  'Europe/Amsterdam': 'CET',
  'Asia/Tokyo': 'JST',
  'Asia/Manila': 'PHT',
  'UTC': 'UTC',
};

const getTimezoneAbbr = (timezone: string): string => {
  return TIMEZONE_ABBR[timezone] || timezone.split('/').pop()?.replace('_', ' ') || timezone;
};

const getLocalTimezoneAbbr = (): string => {
  try {
    const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_ABBR[localTz] || localTz.split('/').pop()?.replace('_', ' ') || 'Local';
  } catch {
    return 'Local';
  }
};

interface TimeWithLocalHoverProps {
  datetime: string;
  timezone?: string;
  showDate?: boolean;
  className?: string;
}

export const TimeWithLocalHover: React.FC<TimeWithLocalHoverProps> = ({
  datetime,
  timezone,
  showDate = false,
  className = '',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const { displayTime, localTime, localTimezoneAbbr, timezoneAbbr } = useMemo(() => {
    const date = new Date(datetime);
    const tz = timezone || 'UTC';
    const tzAbbr = getTimezoneAbbr(tz);
    const localTzAbbr = getLocalTimezoneAbbr();

    const originalFormatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
      ...(showDate && { month: 'short', day: 'numeric' }),
    });

    const localFormatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(showDate && { month: 'short', day: 'numeric' }),
    });

    return {
      displayTime: originalFormatter.format(date),
      localTime: localFormatter.format(date),
      localTimezoneAbbr: localTzAbbr,
      timezoneAbbr: tzAbbr,
    };
  }, [datetime, timezone, showDate]);

  const userLocalTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return null;
    }
  }, []);

  const isSameTimezone = timezone === userLocalTz;

  return (
    <span
      className={`relative inline-flex items-center cursor-default ${className}`}
      onMouseEnter={() => !isSameTimezone && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span>
        {displayTime} {timezone && <span className="text-gray-500">{timezoneAbbr}</span>}
      </span>

      {showTooltip && !isSameTimezone && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-gray-900 dark:bg-gray-700 text-white rounded shadow-lg whitespace-nowrap z-50">
          {localTime} your time ({localTimezoneAbbr})
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
        </span>
      )}
    </span>
  );
};

export const TIMEZONE_OPTIONS: { value: string; label: string; abbr: string }[] = [
  { value: 'America/New_York', label: 'US Eastern', abbr: 'ET' },
  { value: 'America/Chicago', label: 'US Central', abbr: 'CT' },
  { value: 'America/Los_Angeles', label: 'US Pacific', abbr: 'PT' },
  { value: 'Europe/London', label: 'United Kingdom', abbr: 'GMT' },
  { value: 'Europe/Amsterdam', label: 'Netherlands', abbr: 'CET' },
  { value: 'Asia/Tokyo', label: 'Japan', abbr: 'JST' },
  { value: 'Asia/Manila', label: 'Philippines', abbr: 'PHT' },
  { value: 'UTC', label: 'UTC', abbr: 'UTC' },
];

export const detectLocalTimezone = (): string => {
  try {
    const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const match = TIMEZONE_OPTIONS.find(tz => tz.value === localTz);
    return match ? localTz : 'America/New_York';
  } catch {
    return 'America/New_York';
  }
};

export default TimeWithLocalHover;
